'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from 'react';
import { useTranslations } from 'next-intl';
import { Box, VStack, HStack } from '@terra/ui-styled-system';
import {
  BodyDefault,
  HeadingItem,
  LabelDefault,
  MetaDefault,
  Skeleton,
  IconButton,
  CopyIdButton,
  Scroll,
  Tabs,
  SignalContent,
  SignalDrawerAuditTab,
  Badge,
  ConfirmationDialog,
  toasterApi,
  Tooltip,
  Menu,
  ExecutionNeoIcon,
  Button,
  SignalStageBadge,
  SecurityClassificationBadges,
  SignalSourceTag,
  EntityIcon,
  formatEnumForDisplay,
  ArchivedLifecycleBadge,
  archivedEntityDisplayTitle,
  isArchivedLifecycle,
  isInternalStoryTraceLabel,
} from '@terra/ui-design-system';
import { useMentions } from '../../hooks/useMentions';
import {
  Tick01Icon,
  Clock01Icon,
  LinkSquare02Icon,
  Loading03Icon,
  Message01Icon,
  MessageAdd01Icon,
  MoreVerticalCircle01Icon,
  Edit03Icon,
  FloppyDiskIcon,
  Target03Icon,
  Delete02Icon,
} from '@terra/ui-icons';
import { useSignalDetail } from '../../hooks/useSignalDetail';
import { useSignalRelatedTraces } from '../../hooks/useSignalRelatedTraces';
import { useSignalAuditLogs } from '../../hooks/useSignalAuditLogs';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { useMainContentTabs } from '../../store/layoutStore';
import { useChatStore } from '../../store/chatStore';
import { useSyncNeoAttackFromTraces } from '../../hooks/useSyncNeoAttackFromTraces';
import { useExecutionsStore } from '../../store/executionsStore';
import { useTracesSSE } from '../../hooks/useTracesSSE';
import {
  signalDetailSubTabKey,
  useTabViewStateStore,
} from '../../store/tabViewStateStore';
import { Logger } from '../../electron/logger';
import { terraProxyApiClient } from '../../clients/testerClient';
import { generateExecutionTabTitle } from '../../utils/tabTitleUtils';
import type {
  OpenMainContentTabInput,
  SignalMainContentTab,
} from '../../store/layoutStore';
import type { SignalDto } from '@terra/shared-contracts-signals';
import { toDisplaySignalOrigin } from '@terra/shared-contracts-signals';
import { SignalUpdateInputSchema } from '@terra/shared-contracts-signals';
import type { TracePreviewBaseDto } from '@terra/shared-contracts-trace';
import { SignalStatusSelector } from './SignalStatusSelector';
import { SignalCategorySelector } from './SignalCategorySelector';
import { AssigneeSelector } from './AssigneeSelector';
import { ArchivedActionGate } from '../common/ArchivedActionGate';
import {
  useTargetId,
  useAppTargetId,
  useTargetType,
} from '../../store/projectStore';
import { useSignalTestingScopeEligibility } from '../../hooks/useSignalTestingScopeEligibility';
import { useSignalAttackRun } from '../../hooks/useSignalAttackRun';
import type {
  SignalCategoryType,
  SignalOriginType,
  SignalRankType,
  SignalStageType,
  SignalStatusType,
} from '@terra/shared-schemas-orchestrator';
import {
  SignalOriginSchema,
  SignalRankSchema,
} from '@terra/shared-schemas-orchestrator';
import { useFindingsBySignalId } from '../../hooks/api/useFindingsBySignalId';
import { toLinkedFindingRowData } from '../../hooks/api/signal-related-finding';
import { SignalProvideFeedbackModal } from '../feedback/SignalProvideFeedbackModal';
import { useGlobalContextMenu } from '../../hooks/useGlobalContextMenu';
import { GlobalMenuItemsTypeEnum } from '../../store/globalContextMenuStore';
import { RejectedSignalDetailsView } from './RejectedSignalDetailsView';
import { ObjectContextMarkableField } from '../object-context/ObjectContextMarkableField';
import {
  createSignalObjectContextMention,
  getSignalObjectLabel,
} from '../../utils/objectContextMentionUtils';
import { SignalEditForm } from './SignalEditForm';
import {
  applyEditableFieldsToSignal,
  buildSignalUpdatePayload,
  createEditableFieldsFromSignal,
  hasSignalEditableFieldChanges,
  isUserCreatedSignal,
  type SignalEditableFieldKey,
  type SignalEditableFields,
} from './signalEditFields';
import { useUpdateSignal } from '../../hooks/api/useUpdateSignal';
import { useSignalSourceFilterOptions } from '../../hooks/api/useSignalSourceFilterOptions';
import { token } from '@terra/ui-styled-system';
import { InternalNetworkSignalScenariosSection } from '../internal-network/scenarios/InternalNetworkSignalScenariosSection';
import { TARGET_TYPE } from '../../utils/target-scope.utils';
import {
  getSignalExternalNetworkAssetTab,
  isSignalBearingSurfaceAssetType,
  resolveSignalSurfaceAssetLabel,
} from '../../utils/signalSurfaceAssetNavigation';
import type { SignalAssetTypeType } from '@terra/shared-schemas-orchestrator';

type SignalDetailsMode = 'view' | 'edit';
type SignalDetailsTabProps = {
  tab: SignalMainContentTab;
};
const getEndpointTabData = (
  endpointId: string,
  endpointUrl: string,
): OpenMainContentTabInput => {
  let title = 'Endpoint';

  if (endpointUrl) {
    try {
      title = new URL(endpointUrl).pathname;
    } catch {
      title = endpointUrl;
    }
  }

  return {
    id: `endpoint-${endpointId}`,
    title,
    type: 'endpoint',
    data: { endpointId, endpointUrl },
  };
};

const logger = new Logger('SignalDetailsTab');

export function SignalDetailsTab({ tab }: SignalDetailsTabProps) {
  const t = useTranslations('designSystem.signals.drawer');
  const tSignals = useTranslations('signals');
  const tSignalsFilters = useTranslations('signals.filters');
  const tEdit = useTranslations('designSystem.signals.editDrawer');
  const tSignalsBulk = useTranslations('signals.bulkActions');
  const tAgent = useTranslations('agentExecutions');
  const tScenarios = useTranslations('internalNetwork.scenarios');
  const tCopilotTabTitles = useTranslations('chat.copilot.tabTitles');
  const tLifecycle = useTranslations('designSystem.lifecycle');
  const { navigateInActiveTab, openTab, closeTab } = useMainContentTabs();
  const { openContextMenu } = useGlobalContextMenu();
  const targetId = useTargetId();
  const targetType = useTargetType();
  const appTargetId = useAppTargetId();
  const { hasThirdPartySignals } = useSignalSourceFilterOptions({
    applicationId: appTargetId,
    enabled: Boolean(appTargetId),
  });
  const isInternalNetworkTarget = targetType === TARGET_TYPE.INTERNAL_NETWORK;
  const { addMention } = useMentions();
  const { sendHttpMentionsToChat, appendTextToInput } = useChatStore();
  const sendSelectionToChatLabel = 'Send to Chat';
  const signalId = tab.data.signalId;
  const detailSubTabEntityKey = signalId
    ? signalDetailSubTabKey(signalId)
    : undefined;
  const savedDetailSubTab = useTabViewStateStore((state) =>
    detailSubTabEntityKey
      ? state.detailSubTabs[detailSubTabEntityKey]
      : undefined,
  );
  const setDetailSubTab = useTabViewStateStore(
    (state) => state.setDetailSubTab,
  );
  const activeDetailSubTab = useMemo(() => {
    if (savedDetailSubTab === 'audit') {
      return 'audit';
    }

    if (savedDetailSubTab === 'scenarios' && isInternalNetworkTarget) {
      return 'scenarios';
    }

    return 'overview';
  }, [isInternalNetworkTarget, savedDetailSubTab]);
  const handleDetailSubTabChange = useCallback(
    (nextTab: string) => {
      if (!detailSubTabEntityKey) {
        return;
      }

      setDetailSubTab(detailSubTabEntityKey, nextTab);
    },
    [detailSubTabEntityKey, setDetailSubTab],
  );
  const neoAttackState = useExecutionsStore((state) =>
    signalId ? state.getNeoAttackState(signalId) : undefined,
  );
  const isNeoAttackCompleted = neoAttackState?.status === 'completed';
  // Fetch signal data
  const { signal, isLoading, error, refetch } = useSignalDetail({
    signalId,
    enabled: !!signalId,
  });

  useEffect(() => {
    if (!error) {
      return;
    }

    logger.warn('Failed to load signal detail', {
      signalId,
      error: error instanceof Error ? error.message : String(error),
    });
  }, [error, signalId]);
  const { isEligible: isNeoEligible } = useSignalTestingScopeEligibility({
    signal: signal ?? { endpoint: null },
  });
  const { canStartSignalAttack, startSignalAttack, isSignalAttackRunning } =
    useSignalAttackRun();
  const isNetworkAttackRunning = signalId
    ? isSignalAttackRunning(signalId)
    : false;
  const isNeoAttackQueued = neoAttackState?.status === 'queued';
  const isNeoAttackRunning =
    neoAttackState?.status === 'running' ||
    neoAttackState?.status === 'starting' ||
    isNetworkAttackRunning;
  const {
    linkedTraces: linkedExecutionTraces,
    relatedTraces: relatedExecutionTraces,
    isLoading: isLoadingLinkedExecutionTraces,
    refetch: refetchLinkedExecutionTraces,
  } = useSignalRelatedTraces({
    signalId,
    generationTraces: signal?.generationTraces,
    enabled: !!signalId,
  });
  const isChainingSignal =
    signal?.origin === SignalOriginSchema.enum.FINDING_CHAINING;
  const chainingConstituentFindingIds = useMemo<string[] | undefined>(() => {
    if (signal?.origin !== SignalOriginSchema.enum.FINDING_CHAINING) {
      return undefined;
    }

    return signal.constituentFindingIds;
  }, [signal]);
  const hasConstituents = (chainingConstituentFindingIds?.length ?? 0) > 0;
  const { findings: linkedFindings } = useFindingsBySignalId({
    targetId,
    targetType,
    signalId,
    enabled: !!signalId,
  });
  const { findings: chainedFindings } = useFindingsBySignalId({
    targetId,
    targetType,
    signalId: null,
    findingIds: chainingConstituentFindingIds,
    enabled: !!signalId && isChainingSignal && hasConstituents,
  });
  const {
    auditLogs,
    isLoading: isLoadingAuditLogs,
    refetch: refetchAuditLogs,
  } = useSignalAuditLogs({
    id: signalId || null,
    enabled: !!signalId,
  });
  const dateFormatter = useDateFormatter();
  const [localSignal, setLocalSignal] = useState<SignalDto | null>(null);
  const auditLogRefetchTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  useEffect(() => {
    if (signal) {
      setLocalSignal(signal);
    }
  }, [signal]);
  useEffect(
    () => () => {
      if (auditLogRefetchTimeoutRef.current) {
        clearTimeout(auditLogRefetchTimeoutRef.current);
      }
    },
    [],
  );
  // Callback to refetch signal data when a trace for this signal is updated
  const handleTraceUpdate = useCallback(
    (trace: { signalId?: string | null }) => {
      if (trace.signalId === signalId) {
        refetch();
        refetchLinkedExecutionTraces();
      }
    },
    [signalId, refetch, refetchLinkedExecutionTraces],
  );

  // Subscribe to trace SSE events to refetch signal data when traces are updated
  useTracesSSE({
    applicationId: appTargetId || '',
    enabled: !!appTargetId && !!signalId,
    onTraceUpdate: handleTraceUpdate,
  });
  useSyncNeoAttackFromTraces({
    signalId,
    applicationId: signal?.applicationId || appTargetId,
    traces:
      linkedExecutionTraces.length > 0 ? linkedExecutionTraces : signal?.traces,
  });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [mode, setMode] = useState<SignalDetailsMode>('view');
  const [editableFields, setEditableFields] =
    useState<SignalEditableFields | null>(null);
  const [originalFields, setOriginalFields] =
    useState<SignalEditableFields | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const { updateSignal, isUpdating } = useUpdateSignal();
  const hasUnsavedChanges = useMemo(() => {
    if (!editableFields || !originalFields || mode !== 'edit') {
      return false;
    }

    return hasSignalEditableFieldChanges({ editableFields, originalFields });
  }, [editableFields, originalFields, mode]);
  const canEditSignalContent = useMemo(() => {
    const signalForEdit = localSignal || signal;

    if (!signalForEdit) {
      return false;
    }

    return isUserCreatedSignal({ origin: signalForEdit.origin });
  }, [localSignal, signal]);
  const formatOriginLabel = useCallback(
    (origin: SignalOriginType) => {
      const displayOrigin = toDisplaySignalOrigin(origin);
      const key = `origin.${displayOrigin}`;

      if (!tSignalsFilters.has(key as never)) {
        return formatEnumForDisplay(displayOrigin);
      }

      return tSignalsFilters(key as never);
    },
    [tSignalsFilters],
  );

  useEffect(() => {
    if (mode !== 'view') {
      return;
    }

    const signalForFields = localSignal || signal;

    if (!signalForFields) {
      return;
    }

    const fields = createEditableFieldsFromSignal(signalForFields);

    setEditableFields(fields);
    setOriginalFields(fields);
    setSaveError(null);
  }, [signal, localSignal, mode]);
  const getTraceTabData = useCallback(
    (trace: TracePreviewBaseDto): OpenMainContentTabInput => {
      if (trace.executionId) {
        return {
          id: `agent-execution-${trace.executionId}`,
          title: tAgent('executionTabUntitled'),
          type: 'agent-execution',
          data: { executionId: trace.executionId },
        };
      }

      return {
        id: `executions-${trace.threadId}`,
        title: generateExecutionTabTitle({
          executionTitle: isInternalStoryTraceLabel(trace.name)
            ? undefined
            : trace.name,
          fallback: tAgent('executionTabUntitled'),
        }),
        type: 'executions',
        data: {
          threadId: trace.threadId,
          agentName: trace.name,
          ...(trace.executionId && { executionId: trace.executionId }),
        },
      };
    },
    [tAgent],
  );
  const handleViewTrace = useCallback(
    (trace: TracePreviewBaseDto) => {
      navigateInActiveTab(getTraceTabData(trace));
    },
    [getTraceTabData, navigateInActiveTab],
  );
  const handleTraceContextMenu = useCallback(
    (event: MouseEvent<HTMLElement>, trace: TracePreviewBaseDto) => {
      openContextMenu({
        event,
        items: [
          {
            type: GlobalMenuItemsTypeEnum.OPEN_IN_NEW_TAB,
            tab: getTraceTabData(trace),
          },
        ],
      });
    },
    [getTraceTabData, openContextMenu],
  );
  const handleDeleteClick = useCallback(() => {
    if (isArchivedLifecycle(signal?.lifecycle)) {
      return;
    }

    setIsDeleteDialogOpen(true);
  }, [signal?.lifecycle]);
  const handleConfirmDelete = useCallback(async () => {
    if (!signalId) return;
    setIsDeleting(true);

    try {
      await terraProxyApiClient.deleteSignalsBulk([signalId]);
      toasterApi.success({
        title: 'Signal deleted',
        description: 'The signal has been deleted successfully.',
      });
      setIsDeleteDialogOpen(false);
      closeTab(tab.id);
    } catch (error) {
      console.error('Failed to delete signal:', error);
      toasterApi.error({
        title: 'Failed to delete signal',
        description: 'There was an error deleting the signal.',
      });
    } finally {
      setIsDeleting(false);
    }
  }, [signalId, closeTab, tab.id]);
  const handleFieldChange = useCallback(
    <T extends SignalEditableFieldKey>(
      field: T,
      value: SignalEditableFields[T],
    ) => {
      setEditableFields((previousFields) =>
        previousFields ? { ...previousFields, [field]: value } : previousFields,
      );
      setSaveError(null);
    },
    [],
  );
  const handleEditMode = useCallback(() => {
    const signalForEdit = localSignal || signal;

    if (
      !signalForEdit ||
      !isUserCreatedSignal({ origin: signalForEdit.origin })
    ) {
      return;
    }

    const fields = createEditableFieldsFromSignal(signalForEdit);

    setEditableFields(fields);
    setOriginalFields(fields);
    setSaveError(null);
    setMode('edit');
  }, [localSignal, signal]);
  const resetEditState = useCallback(() => {
    if (originalFields) {
      setEditableFields(originalFields);
    }

    setSaveError(null);
    setMode('view');
  }, [originalFields]);
  const handleCancelEdit = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowCloseConfirmation(true);

      return;
    }

    resetEditState();
  }, [hasUnsavedChanges, resetEditState]);
  const handleConfirmDiscardChanges = useCallback(() => {
    setShowCloseConfirmation(false);
    resetEditState();
  }, [resetEditState]);
  const handleSave = useCallback(async () => {
    if (!signalId || !editableFields || !originalFields) {
      return;
    }

    if (!canEditSignalContent) {
      setSaveError(tEdit('saveError'));

      return;
    }

    const updates = buildSignalUpdatePayload({
      editableFields,
      originalFields,
    });

    if (Object.keys(updates).length === 0) {
      setMode('view');

      return;
    }

    const parsedUpdates = SignalUpdateInputSchema.safeParse(updates);

    if (!parsedUpdates.success) {
      setSaveError(tEdit('saveError'));

      return;
    }

    setSaveError(null);

    try {
      await updateSignal({ signalId, update: parsedUpdates.data });
      setLocalSignal((previousSignal) => {
        if (!previousSignal) {
          return previousSignal;
        }

        return applyEditableFieldsToSignal(previousSignal, updates);
      });
      setOriginalFields({ ...editableFields });
      setMode('view');
      if (auditLogRefetchTimeoutRef.current) {
        clearTimeout(auditLogRefetchTimeoutRef.current);
      }

      auditLogRefetchTimeoutRef.current = setTimeout(() => {
        refetchAuditLogs();
        auditLogRefetchTimeoutRef.current = null;
      }, 1000);
      toasterApi.success({
        title: tEdit('saveSuccessTitle'),
        description: tEdit('saveSuccessDescription'),
      });
    } catch (error) {
      logger.warn('Failed to update signal', {
        signalId,
        error: error instanceof Error ? error.message : String(error),
      });
      setSaveError(tEdit('saveError'));
    }
  }, [
    signalId,
    editableFields,
    originalFields,
    canEditSignalContent,
    updateSignal,
    refetchAuditLogs,
    tEdit,
  ]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 's') {
        event.preventDefault();
        if (mode === 'edit' && !isUpdating && hasUnsavedChanges) {
          void handleSave();
        }
      }
    };

    if (mode === 'edit') {
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [mode, isUpdating, hasUnsavedChanges, handleSave]);
  const updateLocalSignal = useCallback(
    (updates: {
      stage?: SignalStageType;
      status?: SignalStatusType | null;
      category?: SignalCategoryType;
      assigneeId?: string | null;
    }) => {
      setLocalSignal((prev) => (prev ? { ...prev, ...updates } : null));
      if (auditLogRefetchTimeoutRef.current) {
        clearTimeout(auditLogRefetchTimeoutRef.current);
      }

      // Refetch audit logs after the server writes the audit entry
      auditLogRefetchTimeoutRef.current = setTimeout(() => {
        refetchAuditLogs();
        auditLogRefetchTimeoutRef.current = null;
      }, 1000);
    },
    [refetchAuditLogs],
  );
  const handleNeoAttack = useCallback(() => {
    if (!signalId) return;
    void startSignalAttack({ signalId, isEligible: isNeoEligible });
  }, [isNeoEligible, signalId, startSignalAttack]);
  const neoActionTooltip = isArchivedLifecycle(signal?.lifecycle)
    ? tLifecycle('archivedSignalActionTooltip')
    : isNeoAttackQueued
      ? tSignalsBulk('neoQueuedTitle')
      : isNeoAttackRunning
        ? 'Neo is running...'
        : isNeoAttackCompleted
          ? 'Neo completed!'
          : !isNeoEligible
            ? tSignalsBulk('neoOutOfTestingScopeSingleTooltip')
            : 'Send to Neo';
  const isNeoActionDisabled =
    isArchivedLifecycle(signal?.lifecycle) ||
    isNeoAttackRunning ||
    isNeoAttackQueued ||
    !isNeoEligible ||
    !canStartSignalAttack;
  const handleAttack = useCallback(() => {
    if (!signal || !signalId || isArchivedLifecycle(signal.lifecycle)) return;
    const label = getSignalObjectLabel(signal);

    addMention({
      id: `signal-${signalId}`,
      label,
      icon: <Target03Icon size={12} />,
      type: 'child',
      mentionEntity: {
        entityType: 'Signal',
        entityId: signalId,
        label,
        extraData: { signal },
      },
    });
    setTimeout(() => {
      appendTextToInput('Attack this signal');
    }, 0);
  }, [signal, signalId, addMention, appendTextToInput]);
  const handleSendFieldContextToChat = useCallback(
    ({
      field,
      fieldLabel,
      excerpt,
      lineStart,
      lineEnd,
    }: {
      field: string;
      fieldLabel: string;
      excerpt: string;
      lineStart?: number;
      lineEnd?: number;
    }) => {
      const activeSignal = localSignal || signal;

      if (!activeSignal) {
        return;
      }

      const mention = createSignalObjectContextMention(activeSignal, {
        selection: {
          field,
          lineStart,
          lineEnd,
          excerpt,
        },
        instruction: `Refine the "${fieldLabel}" field using the selected excerpt.`,
      });

      sendHttpMentionsToChat({
        mentions: [mention],
        contextMessage: `Refine the ${fieldLabel} field for this signal.`,
        linkedObject: {
          kind: 'Signal',
          id: activeSignal.id,
          label: getSignalObjectLabel(activeSignal),
        },
      });
    },
    [localSignal, signal, sendHttpMentionsToChat],
  );
  const renderMarkableSection = useCallback(
    ({
      field,
      fieldLabel,
      fullText,
      children,
    }: {
      field: string;
      fieldLabel: string;
      fullText: string;
      children: React.ReactNode;
    }) => (
      <ObjectContextMarkableField
        field={field}
        fieldLabel={fieldLabel}
        fullText={fullText}
        sendToChatLabel={sendSelectionToChatLabel}
        onSendSelection={handleSendFieldContextToChat}
      >
        {children}
      </ObjectContextMarkableField>
    ),
    [handleSendFieldContextToChat, sendSelectionToChatLabel],
  );
  const handleNavigateToEndpoint = useCallback(
    (endpointId: string, endpointUrl: string) => {
      navigateInActiveTab(getEndpointTabData(endpointId, endpointUrl));
    },
    [navigateInActiveTab],
  );
  const handleEndpointContextMenu = useCallback(
    (
      event: MouseEvent<HTMLElement>,
      endpointId: string,
      endpointUrl: string,
    ) => {
      openContextMenu({
        event,
        items: [
          {
            type: GlobalMenuItemsTypeEnum.OPEN_IN_NEW_TAB,
            tab: getEndpointTabData(endpointId, endpointUrl),
          },
        ],
      });
    },
    [openContextMenu],
  );
  const getSurfaceAssetTabData = useCallback(
    (assetId: string, assetType: SignalAssetTypeType) =>
      getSignalExternalNetworkAssetTab({
        assetId,
        assetType,
        assetSummary: signal?.targetSummary,
        targetType,
      }),
    [signal?.targetSummary, targetType],
  );
  const handleNavigateToSurfaceAsset = useCallback(
    (assetId: string, assetType: SignalAssetTypeType) => {
      const tabData = getSurfaceAssetTabData(assetId, assetType);

      if (!tabData) {
        return;
      }

      navigateInActiveTab(tabData);
    },
    [getSurfaceAssetTabData, navigateInActiveTab],
  );
  const handleSurfaceAssetContextMenu = useCallback(
    (
      event: MouseEvent<HTMLElement>,
      assetId: string,
      assetType: SignalAssetTypeType,
    ) => {
      const tabData = getSurfaceAssetTabData(assetId, assetType);

      if (!tabData) {
        return;
      }

      openContextMenu({
        event,
        items: [
          {
            type: GlobalMenuItemsTypeEnum.OPEN_IN_NEW_TAB,
            tab: tabData,
          },
        ],
      });
    },
    [getSurfaceAssetTabData, openContextMenu],
  );
  const getFindingTabData = useCallback(
    (findingId: string): OpenMainContentTabInput => ({
      id: `finding-${findingId}`,
      title: tCopilotTabTitles('findingDetails'),
      type: 'finding',
      data: { findingId, mode: 'view' },
    }),
    [tCopilotTabTitles],
  );
  const handleNavigateToFinding = useCallback(
    (findingId: string) => {
      navigateInActiveTab(getFindingTabData(findingId));
    },
    [navigateInActiveTab, getFindingTabData],
  );
  const handleFindingsContextMenu = useCallback(
    (event: MouseEvent<HTMLElement>, findingId: string) => {
      openContextMenu({
        event,
        items: [
          {
            type: GlobalMenuItemsTypeEnum.OPEN_IN_NEW_TAB,
            tab: getFindingTabData(findingId),
          },
        ],
      });
    },
    [openContextMenu, getFindingTabData],
  );
  const handleProvideFeedbackSubmit = useCallback(
    async ({
      rating,
      feedback,
    }: {
      rating: 'positive' | 'negative';
      feedback?: string;
    }) => {
      if (!signalId) {
        return { success: false };
      }

      const nextRank: SignalRankType =
        rating === 'positive'
          ? SignalRankSchema.enum.APPROVED
          : SignalRankSchema.enum.REJECTED;

      try {
        const feedbackNote = feedback?.trim();

        await terraProxyApiClient.updateSignal(signalId, {
          rank: nextRank,
          ...(feedbackNote ? { note: feedbackNote } : {}),
        });
        setLocalSignal((prevSignal) =>
          prevSignal ? { ...prevSignal, rank: nextRank } : prevSignal,
        );

        return { success: true };
      } catch (error) {
        console.error('Failed to submit signal feedback:', error);
        toasterApi.error({
          title: 'Failed to submit feedback',
          description: 'Please try again later.',
        });

        return { success: false };
      }
    },
    [signalId],
  );

  // Loading state
  if (isLoading) {
    return (
      <Box h="full" w="full" p="6">
        <VStack gap="4" alignItems="flex-start" w="full">
          <Skeleton h={8} w="200px" />
          <Skeleton h={6} w="300px" />
          <Skeleton h={100} w="full" />
        </VStack>
      </Box>
    );
  }

  if (error || !signalId || !signal) {
    return (
      <Box
        h="full"
        w="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <VStack gap="4" textAlign="center">
          <Target03Icon size={48} />
          <HeadingItem color="fg.default">
            {tSignals('notFoundTitle')}
          </HeadingItem>
          <BodyDefault color="fg.muted">
            {tSignals('notFoundDescription')}
          </BodyDefault>
        </VStack>
      </Box>
    );
  }

  const displaySignal = localSignal || signal;
  const isArchived = isArchivedLifecycle(displaySignal.lifecycle);
  const archivedActionTooltip = tLifecycle('archivedSignalActionTooltip');
  const signalTitle = getSignalObjectLabel(displaySignal);
  const headerTitle = archivedEntityDisplayTitle({
    title: signalTitle,
    isEditMode: mode === 'edit',
    editLabel: tEdit('edit'),
  });
  const viewNeoLabel = tSignalsBulk('viewNeo');
  const surfaceAssetLabel =
    displaySignal.assetId &&
    isSignalBearingSurfaceAssetType(displaySignal.assetType)
      ? resolveSignalSurfaceAssetLabel({
          assetSummary: displaySignal.targetSummary,
          assetType: displaySignal.assetType,
          assetId: displaySignal.assetId,
        })
      : undefined;

  if (tab.data.rejected) {
    return (
      <RejectedSignalDetailsView
        signal={displaySignal}
        onNavigateToEndpoint={handleNavigateToEndpoint}
        onEndpointContextMenu={handleEndpointContextMenu}
        onNavigateToSurfaceAsset={handleNavigateToSurfaceAsset}
        onSurfaceAssetContextMenu={handleSurfaceAssetContextMenu}
        surfaceAssetLabel={surfaceAssetLabel}
        renderMarkableSection={renderMarkableSection}
      />
    );
  }

  const confidenceValue = displaySignal.confidence;
  const hasConfidenceValue =
    typeof confidenceValue === 'number' && Number.isFinite(confidenceValue);
  const confidenceDisplay = hasConfidenceValue ? `${confidenceValue}/5` : null;
  const confidenceColorScheme = !hasConfidenceValue
    ? 'gray'
    : confidenceValue >= 4
      ? 'green'
      : confidenceValue >= 3
        ? 'yellow'
        : 'red';

  return (
    <Box h="full" w="full" overflow="hidden">
      <VStack gap="0" h="full" alignItems="stretch">
        {/* Header */}
        <Box
          px="4"
          py="3"
          borderBottomWidth="1px"
          borderColor="border.subtle"
          bg="bg.default"
        >
          <VStack gap="2" alignItems="flex-start" w="full">
            <HStack gap="3" alignItems="center" w="full">
              <EntityIcon
                type="signal"
                appearance="highlighted"
                density="large"
              />
              <Tooltip title={headerTitle} maxW="700px">
                <Box flex="1" minW="0">
                  <HeadingItem
                    color="fg.default"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {headerTitle}
                  </HeadingItem>
                </Box>
              </Tooltip>
              <ArchivedLifecycleBadge lifecycle={displaySignal.lifecycle} />
              <HStack gap="2">
                {mode === 'view' && canEditSignalContent && (
                  <ArchivedActionGate
                    isArchived={isArchived}
                    tooltip={archivedActionTooltip}
                  >
                    <Tooltip title={tEdit('edit')}>
                      <IconButton
                        variant="ghost"
                        size="xs"
                        aria-label={tEdit('edit')}
                        onClick={handleEditMode}
                      >
                        <Edit03Icon size={14} />
                      </IconButton>
                    </Tooltip>
                  </ArchivedActionGate>
                )}

                <Tooltip title={neoActionTooltip}>
                  <Box display="inline-flex" flexShrink={0}>
                    <IconButton
                      size="xs"
                      variant="ghost"
                      colorScheme={isNeoAttackCompleted ? 'green' : 'purple'}
                      disabled={isNeoActionDisabled}
                      aria-label={tSignalsBulk('sendToNeo')}
                      onClick={handleNeoAttack}
                    >
                      {isNeoAttackQueued ? (
                        <Clock01Icon size={14} />
                      ) : isNeoAttackRunning ? (
                        <Box animation="spin 1s linear infinite" display="flex">
                          <Loading03Icon size={14} />
                        </Box>
                      ) : isNeoAttackCompleted ? (
                        <Tick01Icon size={14} />
                      ) : (
                        <ExecutionNeoIcon size={14} />
                      )}
                    </IconButton>
                  </Box>
                </Tooltip>

                {(isNeoAttackRunning || isNeoAttackCompleted) &&
                  neoAttackState?.threadId && (
                    <Tooltip title={viewNeoLabel}>
                      <IconButton
                        size="xs"
                        variant="ghost"
                        colorScheme="purple"
                        aria-label={viewNeoLabel}
                        onClick={() => {
                          openTab({
                            id: `executions-${neoAttackState.threadId}`,
                            title: tAgent('executionTabTitle', {
                              objective: 'Neo',
                            }),
                            type: 'executions',
                            data: { threadId: neoAttackState.threadId },
                          });
                        }}
                      >
                        <LinkSquare02Icon size={14} />
                      </IconButton>
                    </Tooltip>
                  )}

                <ArchivedActionGate
                  isArchived={isArchived}
                  tooltip={archivedActionTooltip}
                >
                  <Tooltip title={tSignalsBulk('sendToCopilot')}>
                    <IconButton
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      aria-label={tSignalsBulk('sendToCopilot')}
                      onClick={handleAttack}
                    >
                      <Message01Icon size={14} />
                    </IconButton>
                  </Tooltip>
                </ArchivedActionGate>

                <CopyIdButton id={displaySignal.id} entityName={t('title')} />

                <Menu.Root
                  positioning={{ strategy: 'fixed', placement: 'bottom-end' }}
                >
                  <Menu.Trigger asChild>
                    <IconButton
                      variant="ghost"
                      size="xs"
                      aria-label="Signal actions"
                    >
                      <MoreVerticalCircle01Icon size={14} />
                    </IconButton>
                  </Menu.Trigger>
                  <Menu.Positioner>
                    <Menu.Content minW="160px" py="1">
                      <Menu.Item
                        value="provide-feedback"
                        fontSize="xs"
                        py="1"
                        px="2"
                        disabled={isArchived}
                        title={isArchived ? archivedActionTooltip : undefined}
                        onClick={
                          isArchived
                            ? undefined
                            : () => setIsFeedbackModalOpen(true)
                        }
                      >
                        <HStack gap="1.5">
                          <MessageAdd01Icon size={12} />
                          <LabelDefault as="span">
                            {tSignals('provideFeedback')}
                          </LabelDefault>
                        </HStack>
                      </Menu.Item>
                      <Menu.Item
                        value="delete-signal"
                        fontSize="xs"
                        py="1"
                        px="2"
                        disabled={isArchived}
                        title={isArchived ? archivedActionTooltip : undefined}
                        onClick={isArchived ? undefined : handleDeleteClick}
                      >
                        <HStack gap="1.5">
                          <Delete02Icon size={12} />
                          <LabelDefault as="span">
                            {tSignals('deleteSignal')}
                          </LabelDefault>
                        </HStack>
                      </Menu.Item>
                    </Menu.Content>
                  </Menu.Positioner>
                </Menu.Root>
              </HStack>
            </HStack>

            {mode === 'view' && (
              <HStack
                gap="3"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                w="full"
              >
                <HStack gap="1.5" alignItems="center" flexWrap="wrap" minW={0}>
                  {displaySignal.isAppWide === true &&
                    targetType !== TARGET_TYPE.AI_APPLICATION && (
                      <Tooltip title={t('appWideSignalTooltip')}>
                        <Badge
                          size="sm"
                          variant="emphasis"
                          bg={token('colors.bg.vulnerability.hot')}
                          borderColor={token('colors.border.vulnerability.hot')}
                        >
                          {t('appWideSignal')}
                        </Badge>
                      </Tooltip>
                    )}
                  <ArchivedActionGate
                    isArchived={isArchived}
                    tooltip={archivedActionTooltip}
                  >
                    <SignalCategorySelector
                      signalId={signalId}
                      currentCategory={displaySignal.category}
                      disabled={isArchived}
                      onCategoryChange={(category) => {
                        updateLocalSignal({ category });
                      }}
                    />
                  </ArchivedActionGate>
                  {hasThirdPartySignals === true && displaySignal.source ? (
                    <Badge variant="outline" size="sm" maxW="full" truncate>
                      <SignalSourceTag
                        source={displaySignal.source}
                        size={12}
                      />
                    </Badge>
                  ) : null}
                  <SecurityClassificationBadges
                    cweIds={displaySignal.cweIds}
                    owaspCategory={displaySignal.owaspCategory}
                    density="compact"
                  />
                  {confidenceDisplay && (
                    <Tooltip title={`Confidence: ${confidenceDisplay}`}>
                      <Badge
                        size="sm"
                        variant="fill"
                        colorScheme={confidenceColorScheme}
                      >
                        {confidenceDisplay}
                      </Badge>
                    </Tooltip>
                  )}
                </HStack>
                <HStack
                  gap="2"
                  alignItems="center"
                  flexWrap="wrap"
                  flexShrink={0}
                >
                  <SignalStageBadge stage={displaySignal.stage} />
                  <ArchivedActionGate
                    isArchived={isArchived}
                    tooltip={archivedActionTooltip}
                  >
                    <SignalStatusSelector
                      signalId={signalId}
                      currentStage={displaySignal.stage}
                      currentStatus={displaySignal.status}
                      currentBlockedReason={displaySignal.blockedReason}
                      disabled={isArchived}
                      relatedFinding={
                        linkedFindings[0]
                          ? {
                              id: linkedFindings[0].id,
                              title: linkedFindings[0].title,
                            }
                          : undefined
                      }
                      onOpenRelatedFinding={handleNavigateToFinding}
                      onStatusChange={({ stage, status }) => {
                        updateLocalSignal({ stage, status });
                      }}
                    />
                  </ArchivedActionGate>
                  <ArchivedActionGate
                    isArchived={isArchived}
                    tooltip={archivedActionTooltip}
                  >
                    <AssigneeSelector
                      kind="signal"
                      signalId={signalId}
                      selectedId={displaySignal.assigneeId || undefined}
                      showName
                      disabled={isArchived}
                      onAssigneeChange={(assigneeId) => {
                        updateLocalSignal({ assigneeId: assigneeId ?? null });
                        refetch();
                      }}
                    />
                  </ArchivedActionGate>
                </HStack>
              </HStack>
            )}
          </VStack>
        </Box>

        {/* Inline signal feedback */}
        {isFeedbackModalOpen && !isArchived && (
          <Box px="4" py="2">
            <SignalProvideFeedbackModal
              isOpen={isFeedbackModalOpen}
              onClose={() => setIsFeedbackModalOpen(false)}
              onSubmit={handleProvideFeedbackSubmit}
              renderMode="inline"
            />
          </Box>
        )}

        {mode === 'edit' && editableFields ? (
          <Box flex="1" overflow="hidden">
            <Scroll orientation="vertical" showScrollbar>
              <Box p="4">
                <SignalEditForm
                  editableFields={editableFields}
                  onFieldChange={handleFieldChange}
                  saveError={saveError}
                />
              </Box>
            </Scroll>
          </Box>
        ) : (
          <Tabs.Root
            value={activeDetailSubTab}
            onValueChange={({ value }) => handleDetailSubTabChange(value)}
            size="sm"
            w="full"
            flex="1"
            display="flex"
            flexDirection="column"
            overflow="hidden"
            lazyMount
            unmountOnExit
          >
            <Tabs.List justifyContent="flex-start" px="4">
              <Tabs.Trigger value="overview" fontSize="sm" px="3" py="2">
                {tSignals('detailTabs.overview')}
              </Tabs.Trigger>
              {isInternalNetworkTarget ? (
                <Tabs.Trigger value="scenarios" fontSize="sm" px="3" py="2">
                  {tScenarios('title')}
                </Tabs.Trigger>
              ) : null}
              <Tabs.Trigger value="audit" fontSize="sm" px="3" py="2">
                {tSignals('detailTabs.auditTrail')}
              </Tabs.Trigger>
              <Tabs.Indicator />
            </Tabs.List>

            <Tabs.Content value="overview" flex="1" overflow="hidden" pt="0">
              <Box h="full" overflow="hidden">
                <Scroll orientation="vertical" showScrollbar>
                  <Box p="4">
                    <SignalContent
                      signal={displaySignal}
                      formatOriginLabel={formatOriginLabel}
                      traces={
                        isLoadingLinkedExecutionTraces
                          ? undefined
                          : relatedExecutionTraces
                      }
                      linkedFindings={linkedFindings.map(
                        toLinkedFindingRowData,
                      )}
                      chainedFindings={chainedFindings.map(
                        toLinkedFindingRowData,
                      )}
                      showMetadata={false}
                      onViewTrace={handleViewTrace}
                      onTraceContextMenu={handleTraceContextMenu}
                      onNavigateToEndpoint={handleNavigateToEndpoint}
                      onEndpointContextMenu={handleEndpointContextMenu}
                      onNavigateToSurfaceAsset={
                        targetType === TARGET_TYPE.EXTERNAL_NETWORK
                          ? handleNavigateToSurfaceAsset
                          : undefined
                      }
                      onSurfaceAssetContextMenu={
                        targetType === TARGET_TYPE.EXTERNAL_NETWORK
                          ? handleSurfaceAssetContextMenu
                          : undefined
                      }
                      surfaceAssetLabel={surfaceAssetLabel}
                      onNavigateToFinding={handleNavigateToFinding}
                      onFindingsContextMenu={handleFindingsContextMenu}
                      shouldDisplayRuns={true}
                      renderMarkableSection={renderMarkableSection}
                    />
                  </Box>
                </Scroll>
              </Box>
            </Tabs.Content>

            {isInternalNetworkTarget && targetId ? (
              <Tabs.Content value="scenarios" flex="1" overflow="hidden" pt="0">
                <Box h="full" overflow="hidden">
                  <Scroll orientation="vertical" showScrollbar>
                    <Box p="4">
                      <InternalNetworkSignalScenariosSection
                        signalId={signalId}
                        networkId={targetId}
                        signal={displaySignal}
                        variant="tab"
                      />
                    </Box>
                  </Scroll>
                </Box>
              </Tabs.Content>
            ) : null}

            <Tabs.Content value="audit" flex="1" overflow="hidden" pt="0">
              <Box h="full" overflow="hidden">
                <Scroll orientation="vertical" showScrollbar>
                  <Box p="4">
                    <SignalDrawerAuditTab
                      auditLogs={auditLogs}
                      isLoading={isLoadingAuditLogs}
                      dateFormatter={dateFormatter}
                    />
                  </Box>
                </Scroll>
              </Box>
            </Tabs.Content>
          </Tabs.Root>
        )}

        {mode === 'edit' && (
          <HStack
            gap={2}
            px="4"
            py="3"
            borderTopWidth="1px"
            borderTopColor="border.default"
            bg="bg.subtle"
            w="full"
            flexShrink={0}
            justifyContent="space-between"
            alignItems="center"
          >
            <MetaDefault color="fg.muted">
              {hasUnsavedChanges
                ? tEdit('unsavedChanges')
                : tEdit('allChangesSaved')}
            </MetaDefault>
            <HStack gap={2}>
              <Button
                variant="outline"
                onClick={handleCancelEdit}
                disabled={isUpdating}
                size="sm"
              >
                {tEdit('cancel')}
              </Button>
              <Button
                onClick={() => {
                  void handleSave();
                }}
                disabled={!hasUnsavedChanges || isUpdating}
                loading={isUpdating}
                size="sm"
                colorScheme="blue"
              >
                <FloppyDiskIcon size={14} />
                {tEdit('save')}
              </Button>
            </HStack>
          </HStack>
        )}
      </VStack>
      <ConfirmationDialog
        isOpen={showCloseConfirmation}
        onOpenChange={({ open }) => {
          setShowCloseConfirmation(open);
        }}
        onConfirm={handleConfirmDiscardChanges}
        variant="warning"
        title={tEdit('confirmClose.title')}
        description={tEdit('confirmClose.description')}
        confirmText={tEdit('confirmClose.confirm')}
        cancelText={tEdit('confirmClose.cancel')}
      />
      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={({ open }) => {
          setIsDeleteDialogOpen(open);
        }}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
        variant="destructive"
        title="Delete Signal"
        description="Are you sure you want to delete this signal? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </Box>
  );
}
